/**
 * @file routes/_app/analytics/certificates.tsx
 * @description 發證分析頁。
 *
 * 顯示月度發證趨勢、證書狀態分布（PieChart）。
 * 支援日期範圍篩選 + Excel 匯出。
 *
 * 視覺：PageShell + StatCard + SectionCard + EmptyState（recharts BarChart + PieChart）
 */

import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useState } from 'react';
import { Button, DatePicker, Space, Spin } from 'antd';
import { message } from '@/lib/antd-app';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { FileDown, RefreshCw, ShieldCheck, AlertTriangle, TrendingUp } from 'lucide-react';
import { type Dayjs } from 'dayjs';
import { useCertificateTrend } from '@/hooks/useAnalytics';
import { PageShell, SectionCard, StatCard, EmptyState } from '@/components/shell';
import { color, space } from '@/styles/tokens';

const { RangePicker } = DatePicker;

/** TanStack Router 路由定義 */
export const Route = createFileRoute('/_app/analytics/certificates')({
  component: AnalyticsCertificatesPage,
});

const CHART_COLORS = ['#059669', '#d97706', '#e11d48', '#4f46e5', '#0891b2', '#7c3aed'];

/** 發證分析頁 */
function AnalyticsCertificatesPage() {
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [exporting, setExporting] = useState(false);

  const range = {
    from: dateRange?.[0]?.format('YYYY-MM-DD'),
    to: dateRange?.[1]?.format('YYYY-MM-DD'),
  };

  const { data, isLoading, isFetching, refetch } = useCertificateTrend(range);

  const trendData = data?.trend ?? [];
  const statusDist = data?.statusDistribution ?? [];

  /* ── 匯出 ── */
  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ type: 'certificates' });
      if (range.from) params.set('from', range.from);
      if (range.to) params.set('to', range.to);
      const resp = await fetch(`/api/analytics/export?${params.toString()}`, {
        credentials: 'same-origin',
      });
      if (!resp.ok) throw new Error('Export failed');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `zhcb-certificates-analytics-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      message.success('匯出成功');
    } catch (err) {
      message.error('匯出失敗');
      console.error('export certificates analytics failed', err);
    } finally {
      setExporting(false);
    }
  }, [range]);

  return (
    <PageShell
      title="發證分析"
      description="月度發證趨勢與證書狀態分布"
      actions={
        <Space>
          <Button
            icon={<RefreshCw size={14} />}
            onClick={() => void refetch()}
            loading={isFetching}
          >
            刷新
          </Button>
          <Button icon={<FileDown size={14} />} loading={exporting} onClick={handleExport}>
            匯出 Excel
          </Button>
        </Space>
      }
    >
      {/* 篩選 */}
      <div style={{ marginBottom: space[4] }}>
        <SectionCard title="篩選條件">
          <RangePicker
            value={dateRange}
            onChange={(v) => setDateRange(v)}
            placeholder={['開始日期', '結束日期']}
            format="YYYY-MM-DD"
          />
        </SectionCard>
      </div>

      {/* KPI 卡片 */}
      {!isLoading && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: space[4],
            marginBottom: space[4],
          }}
        >
          <StatCard
            title="有效證書數"
            value={data?.totalValid ?? 0}
            suffix="張"
            tone="success"
            icon={<ShieldCheck size={18} />}
          />
          <StatCard
            title="即將到期"
            value={data?.totalExpiringSoon ?? 0}
            suffix="張"
            tone={data?.totalExpiringSoon ? 'warning' : 'neutral'}
            icon={<AlertTriangle size={18} />}
          />
          <StatCard
            title="總發證量"
            value={trendData.reduce((sum, r) => sum + r.issued, 0)}
            suffix="張"
            tone="brand"
            icon={<TrendingUp size={18} />}
          />
        </div>
      )}

      {/* 圖表區 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: space[4] }}>
        {/* 月度發證 BarChart */}
        <SectionCard title="月度發證趨勢">
          {isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: space[8] }}>
              <Spin />
            </div>
          ) : trendData.length === 0 ? (
            <EmptyState title="暫無發證資料" size="sm" />
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={trendData} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                <RechartTooltip />
                <Bar
                  dataKey="issued"
                  name="發證數"
                  fill={color.brand.primary}
                  radius={[3, 3, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </SectionCard>

        {/* 狀態分布 PieChart */}
        <SectionCard title="證書狀態分布">
          {isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: space[8] }}>
              <Spin />
            </div>
          ) : statusDist.length === 0 ? (
            <EmptyState title="暫無證書資料" size="sm" />
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={statusDist}
                  dataKey="count"
                  nameKey="status"
                  cx="50%"
                  cy="50%"
                  outerRadius={90}
                  label={(props) => {
                    const p = props as unknown as { status?: string; count?: number };
                    return `${p.status ?? ''}(${p.count ?? ''})`;
                  }}
                  labelLine={false}
                >
                  {statusDist.map((_, idx) => (
                    <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <RechartTooltip formatter={(v, n) => [v, n]} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </SectionCard>
      </div>
    </PageShell>
  );
}
